<template>
	<div class="min-h-screen bg-gray-50 font-sans">
		<!-- Header -->
		<header class="bg-white border-b border-gray-200 py-4 px-3">
			<div class="w-full flex justify-between items-center flex-wrap gap-2">
				<div>
					<h1 class="text-2xl font-semibold text-gray-900 m-0">Offering Options</h1>
					<p class="text-sm text-gray-500 mt-1 mb-0 ml-0 mr-0">Manage offering options and translations</p>
				</div>
				<button
					type="button"
					class="inline-flex items-center gap-2 bg-orange-7 hover:bg-orange-6 text-white py-2.5 px-4 rounded-lg text-sm font-medium border-none cursor-pointer transition-colors"
					@click="showCreateModal = true"
				>
					<svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
						<path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
					</svg>
					Create New
				</button>
			</div>
		</header>

		<!-- Main Content -->
		<main class="w-full p-3">
			<!-- Card -->
			<div class="bg-white rounded-xl shadow-sm overflow-hidden">
				<!-- Card Header -->
				<div class="py-4 px-6 border-b border-gray-200 flex flex-wrap justify-between items-center gap-4">
					<div class="flex items-center gap-3">
						<h2 class="text-lg font-semibold text-gray-900 m-0">Offering Options</h2>
						<span class="inline-flex items-center py-1 px-2.5 text-xs font-medium bg-orange-2 text-orange-7 rounded-full">
							{{ totalRows }} total
						</span>
					</div>
					<div class="flex items-center gap-3 flex-wrap">
						<!-- Language Filter -->
						<div class="relative">
							<select
								v-model="selectedLanguage"
								class="py-2 pr-8 pl-3 border border-gray-300 rounded-lg text-sm bg-white cursor-pointer outline-none min-w-32"
								@change="loadTranslations"
							>
								<option v-for="lang in languages" :key="lang.id" :value="lang.id">
									{{ lang.name }}
								</option>
							</select>
						</div>
						<!-- Search -->
						<div class="relative">
							<svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
								<path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
							</svg>
							<input
								v-model="searchQuery"
								type="text"
								placeholder="Search..."
								class="py-2 pr-3 pl-9 border border-gray-300 rounded-lg text-sm w-56 outline-none transition-colors focus:border-orange-7 focus:ring-2 focus:ring-orange-7/10"
								@input="applyFilters"
							>
						</div>
					</div>
				</div>

				<!-- Table -->
				<div class="overflow-x-auto">
					<!-- Loading State -->
					<div v-if="isLoading" class="flex justify-center items-center py-16 px-4">
						<svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
							<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
							<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
						</svg>
						<span class="ml-3 text-gray-500 text-sm">Loading offering options...</span>
					</div>

					<!-- Empty State -->
					<div v-else-if="filteredItems.length === 0" class="text-center py-16 px-4">
						<svg class="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
							<path stroke-linecap="round" stroke-linejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
						</svg>
						<h3 class="text-base font-medium text-gray-700 mb-1 mt-0">No offering options</h3>
						<p class="text-sm text-gray-500 m-0">Get started by creating a new offering option.</p>
					</div>

					<!-- Data Table -->
					<table v-else class="w-full border-collapse">
						<thead>
							<tr class="bg-gray-50">
								<th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">ID</th>
								<th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
								<th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Header</th>
								<th class="py-3 px-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Description</th>
								<th class="py-3 px-6 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="item in paginatedItems"
								:key="item.id"
								class="border-t border-gray-200 transition-colors hover:bg-gray-50"
							>
								<td class="py-4 px-6 text-sm text-gray-500">{{ item.id }}</td>
								<td class="py-4 px-6 text-sm font-medium text-gray-900">{{ item.name }}</td>
								<td class="py-4 px-6 text-sm">
									<input
										:value="item.header || ''"
										type="text"
										placeholder="Enter header..."
										class="w-full py-1.5 px-2 border border-gray-200 rounded-md text-sm outline-none transition-colors focus:border-orange-7"
										@blur="saveTranslation(item, $event.target.value)"
									>
								</td>
								<td class="py-4 px-6 text-sm text-gray-500">{{ item.description || '-' }}</td>
								<td class="py-4 px-6 text-right">
									<div class="inline-flex items-center gap-2">
										<button
											type="button"
											class="inline-flex items-center justify-center w-8 h-8 rounded-md bg-transparent border-none text-gray-500 cursor-pointer transition-colors hover:bg-orange-2 hover:text-orange-7"
											title="Edit"
											@click="editItem(item.id)"
										>
											<svg class="w-4.5 h-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
												<path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
											</svg>
										</button>
										<button
											type="button"
											class="inline-flex items-center justify-center w-8 h-8 rounded-md bg-transparent border-none text-gray-500 cursor-pointer transition-colors hover:bg-red-50 hover:text-red-600"
											title="Delete"
											@click="confirmDelete(item)"
										>
											<svg class="w-4.5 h-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
												<path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
											</svg>
										</button>
									</div>
								</td>
							</tr>
						</tbody>
					</table>
				</div>

				<!-- Pagination -->
				<div v-if="filteredItems.length > 0" class="py-3 px-6 border-t border-gray-200 flex flex-wrap justify-between items-center gap-4 bg-gray-50">
					<div class="flex items-center gap-2 text-sm text-gray-500">
						<span>Showing {{ startRow }} to {{ endRow }} of {{ totalRows }} results</span>
					</div>
					<div class="flex items-center gap-2">
						<select
							v-model="pageSize"
							class="py-1.5 pr-8 pl-3 border border-gray-300 rounded-md text-sm bg-white cursor-pointer outline-none"
						>
							<option :value="10">10 per page</option>
							<option :value="25">25 per page</option>
							<option :value="50">50 per page</option>
							<option :value="100">100 per page</option>
						</select>
						<div class="flex items-center gap-1">
							<button
								type="button"
								:disabled="currentPage === 1"
								class="inline-flex items-center justify-center w-8 h-8 border border-gray-300 rounded-md bg-white cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
								@click="goToPage(currentPage - 1)"
							>
								<svg class="w-4 h-4 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
									<path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
								</svg>
							</button>
							<span class="px-3 text-sm text-gray-700">{{ currentPage }} / {{ totalPages }}</span>
							<button
								type="button"
								:disabled="currentPage === totalPages"
								class="inline-flex items-center justify-center w-8 h-8 border border-gray-300 rounded-md bg-white cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
								@click="goToPage(currentPage + 1)"
							>
								<svg class="w-4 h-4 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
									<path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
								</svg>
							</button>
						</div>
					</div>
				</div>
			</div>
		</main>

		<!-- Create Modal -->
		<div v-if="showCreateModal" class="fixed inset-0 z-50">
			<div class="fixed inset-0 bg-black/50" @click="showCreateModal = false"></div>
			<div class="fixed inset-0 overflow-y-auto">
				<div class="flex min-h-full items-center justify-center p-4">
					<div class="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 shadow-2xl relative z-[51]">
						<h3 class="text-xl font-semibold text-gray-900 m-0">
							Create New Offering Option
						</h3>
						<p class="mt-1 text-sm text-gray-500">
							Add a new offering option.
						</p>
						<div class="mt-6">
							<label class="block text-sm font-medium text-gray-700 mb-2">
								Name <span class="text-red-600">*</span>
							</label>
							<input
								v-model="newItemName"
								type="text"
								placeholder="Enter offering option name"
								class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none box-border focus:border-orange-7 focus:ring-2 focus:ring-orange-7/10"
							>
						</div>
						<div class="mt-6 flex flex-col gap-3">
							<button
								type="button"
								:disabled="isCreating || !newItemName.trim()"
								class="w-full py-2.5 px-4 bg-orange-7 hover:bg-orange-6 text-white rounded-lg text-sm font-medium border-none cursor-pointer transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
								@click="createItem"
							>
								{{ isCreating ? 'Creating...' : 'Create Offering Option' }}
							</button>
							<button
								type="button"
								class="w-full py-2.5 px-4 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded-lg text-sm font-medium cursor-pointer transition-colors"
								@click="showCreateModal = false"
							>
								Cancel
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import Swal from 'sweetalert2';
import OfferingOptionService from 'services/OfferingOptionService';
import LanguageService from 'services/LanguageService';
import TranslationService from 'services/TranslationService';
import { httpClient } from 'utils/http';

export default defineComponent({
	name: 'OfferingOptionsPage',
	setup() {
		const router = useRouter();

		const items = ref<any[]>([]);
		const languages = ref<any[]>([]);
		const selectedLanguage = ref('');
		const translations = ref<Record<string, string>>({});
		const searchQuery = ref('');
		const currentPage = ref(1);
		const pageSize = ref(25);
		const isLoading = ref(true);
		const showCreateModal = ref(false);
		const newItemName = ref('');
		const isCreating = ref(false);

		const filteredItems = computed(() => {
			if (!searchQuery.value.trim()) {
				return items.value;
			}
			const query = searchQuery.value.toLowerCase();
			return items.value.filter(item =>
				item.name?.toLowerCase().includes(query) ||
				item.header?.toLowerCase().includes(query) ||
				item.description?.toLowerCase().includes(query) ||
				String(item.id).includes(query)
			);
		});

		const totalRows = computed(() => filteredItems.value.length);
		const totalPages = computed(() => Math.max(1, Math.ceil(totalRows.value / pageSize.value)));
		const startRow = computed(() => totalRows.value === 0 ? 0 : (currentPage.value - 1) * pageSize.value + 1);
		const endRow = computed(() => Math.min(currentPage.value * pageSize.value, totalRows.value));

		const paginatedItems = computed(() => {
			const start = (currentPage.value - 1) * pageSize.value;
			return filteredItems.value.slice(start, start + pageSize.value);
		});

		watch([searchQuery, pageSize], () => {
			currentPage.value = 1;
		});

		const applyFilters = () => {
			currentPage.value = 1;
		};

		const goToPage = (page: number) => {
			if (page >= 1 && page <= totalPages.value) {
				currentPage.value = page;
			}
		};

		const loadData = async () => {
			isLoading.value = true;
			try {
				const params = new URLSearchParams({ limit: '0' });
				const [optionData, languageData] = await Promise.all([
					OfferingOptionService.getAll(params),
					LanguageService.getAll(params),
				]);
				items.value = optionData;
				languages.value = languageData;

				const defaultLang = languageData.find((l: any) => l.default);
				selectedLanguage.value = defaultLang ? defaultLang.id : languageData[0]?.id || '';

				if (selectedLanguage.value) {
					await loadTranslations();
				}
			} catch (error) {
				console.error('Failed to load data:', error);
			} finally {
				isLoading.value = false;
			}
		};

		const loadTranslations = async () => {
			if (!selectedLanguage.value) return;
			try {
				const params = new URLSearchParams({
					ns: 'offeringoptions',
					lng: selectedLanguage.value,
					limit: '0',
				});
				const data = await TranslationService.getMultiTranslation(params);
				translations.value = data[selectedLanguage.value]?.offeringoptions || {};

				items.value = items.value.map(item => {
					const trans = translations.value[item.id];
					return {
						...item,
						header: trans || '',
					};
				});
			} catch (error) {
				console.error('Failed to load translations:', error);
			}
		};

		const saveTranslation = async (item: any, value: string) => {
			if (item.header === value) return;
			try {
				await httpClient.put('/api/translation', {
					namespace: 'offeringoptions',
					language: selectedLanguage.value,
					bundle: { [item.id]: value },
				});
				item.header = value;
				Swal.fire({
					toast: true,
					position: 'top-end',
					icon: 'success',
					title: 'Translation saved',
					showConfirmButton: false,
					timer: 1500,
					timerProgressBar: true,
				});
			} catch (error: any) {
				console.error('Failed to save translation:', error);
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to save translation',
					icon: 'error',
					customClass: {
						popup: 'windmill-swal-popup',
						title: 'windmill-swal-title',
						htmlContainer: 'windmill-swal-content',
						confirmButton: 'windmill-swal-confirm',
					},
				});
			}
		};

		const editItem = (id: number) => {
			router.push(`/offeringoption/${id}`);
		};

		const confirmDelete = async (item: any) => {
			const result = await Swal.fire({
				title: 'Delete Offering Option?',
				text: `Are you sure you want to delete "${item.name}"? This action cannot be undone.`,
				icon: 'warning',
				showCancelButton: true,
				confirmButtonColor: '#dc2626',
				cancelButtonColor: '#6b7280',
				confirmButtonText: 'Yes, delete it',
				cancelButtonText: 'Cancel',
				customClass: {
					popup: 'windmill-swal-popup',
					title: 'windmill-swal-title',
					htmlContainer: 'windmill-swal-content',
					confirmButton: 'windmill-swal-confirm-danger',
					cancelButton: 'windmill-swal-cancel',
				},
			});

			if (result.isConfirmed) {
				try {
					await OfferingOptionService.delete(item.id);
					items.value = items.value.filter(i => i.id !== item.id);
					Swal.fire({
						toast: true,
						position: 'top-end',
						icon: 'success',
						title: 'Offering option deleted',
						showConfirmButton: false,
						timer: 2000,
						timerProgressBar: true,
					});
				} catch (error: any) {
					Swal.fire({
						title: 'Error',
						text: error.message || 'Failed to delete offering option',
						icon: 'error',
						customClass: {
							popup: 'windmill-swal-popup',
							title: 'windmill-swal-title',
							htmlContainer: 'windmill-swal-content',
							confirmButton: 'windmill-swal-confirm',
						},
					});
				}
			}
		};

		const createItem = async () => {
			if (!newItemName.value.trim()) return;

			isCreating.value = true;
			try {
				const newItem = await OfferingOptionService.create({ name: newItemName.value.trim() });
				Swal.fire({
					toast: true,
					position: 'top-end',
					icon: 'success',
					title: 'Offering option created',
					showConfirmButton: false,
					timer: 2000,
					timerProgressBar: true,
				});
				showCreateModal.value = false;
				newItemName.value = '';
				router.push(`/offeringoption/${newItem.id}`);
			} catch (error: any) {
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to create offering option',
					icon: 'error',
					customClass: {
						popup: 'windmill-swal-popup',
						title: 'windmill-swal-title',
						htmlContainer: 'windmill-swal-content',
						confirmButton: 'windmill-swal-confirm',
					},
				});
			} finally {
				isCreating.value = false;
			}
		};

		onMounted(() => {
			loadData();
		});

		return {
			items,
			languages,
			selectedLanguage,
			searchQuery,
			currentPage,
			pageSize,
			isLoading,
			showCreateModal,
			newItemName,
			isCreating,
			filteredItems,
			totalRows,
			totalPages,
			startRow,
			endRow,
			paginatedItems,
			applyFilters,
			goToPage,
			loadTranslations,
			saveTranslation,
			editItem,
			confirmDelete,
			createItem,
		};
	},
});
</script>

<style>
@keyframes spin {
	from { transform: rotate(0deg); }
	to { transform: rotate(360deg); }
}
</style>
